import { useLocation } from '@gatsbyjs/reach-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'

// Utility function to check if window is defined
const isWindowDefined = () => typeof window !== 'undefined'

const useQueryString = () => {
  const location = useLocation()
  const { search, pathname } = location

  // Initialize query parameters state
  const [queryParams, setQueryParams] = useState(
    () => new URLSearchParams(search || '')
  )

  // Memoize the current query parameters to optimize performance
  const memoizedQueryParams = useMemo(
    () => new URLSearchParams(search || ''),
    [search]
  )

  // Function to get a query parameter by name
  const getQueryParam = useCallback(
    (param) => {
      return queryParams.get(param)
    },
    [queryParams]
  )

  // Function to set a query parameter
  const setQueryParam = useCallback(
    (param, value) => {
      // Clone the existing query parameters
      const newParams = new URLSearchParams(queryParams)
      newParams.set(param, value)

      // Serialize the updated query parameters
      const serializedParams = newParams.toString()

      // Update the URL without reloading the page
      if (isWindowDefined()) {
        const newUrl = serializedParams ? `?${serializedParams}` : pathname
        window.history.replaceState({}, '', newUrl)
      }

      // Update the state with the new query parameters
      setQueryParams(newParams)
    },
    [queryParams, pathname]
  )

  // Debounced version of setQueryParam to limit the frequency of updates
  const debouncedSetQueryParam = useMemo(
    () =>
      debounce((param, value) => {
        setQueryParam(param, value)
      }, 300),
    [setQueryParam]
  )

  useEffect(() => {
    // Update the state when the search string changes
    setQueryParams(memoizedQueryParams)
  }, [memoizedQueryParams])

  useEffect(() => {
    // Cleanup debounced functions on unmount
    return () => {
      debouncedSetQueryParam.cancel()
    }
  }, [debouncedSetQueryParam])

  return {
    getQueryParam,
    setQueryParam: debouncedSetQueryParam, // Use debounced version if needed
  }
}

export default useQueryString
